import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const AdminAccountant1 = () => {
  const { currentUser } = useSelector((state) => state.user || {});
  const [employeeBenefits, setEmployeeBenefits] = useState([]);
  const [benefitReasons, setBenefitReasons] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  useEffect(() => {
    console.log("currentUser:", currentUser);
    console.log("token:", token);

    if (!currentUser?._id || currentUser.userType !== "adminAccountant1") {
      toast.error("Access denied. Accountant 1 privileges required.");
      setLoading(false);
      navigate("/login");
      return;
    }

    const fetchEmployeeBenefits = async () => {
      try {
        console.log("Fetching employee benefits with token:", token);
        const response = await fetch(`${process.env.REACT_APP_API_URL}admin/accountant/benefit/get-all-employee-benefits`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("API response status:", response.status);
        const data = await response.json();
        console.log("API response data:", data);
        if (response.ok) {
          setEmployeeBenefits(data.data || []);
        } else {
          setError(data.message || "Failed to fetch employee benefits");
          toast.error(data.message || "Failed to fetch employee benefits");
        }
      } catch (err) {
        console.error("API error:", err);
        setError("Server error while fetching employee benefits");
        toast.error("Server error while fetching employee benefits");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchEmployeeBenefits();
    } else {
      setError("No authentication token found");
      toast.error("No authentication token found");
      setLoading(false);
      navigate("/login");
    }
  }, [currentUser?._id, currentUser?.userType, token, navigate]);

  const handleBenefitAction = async (benefitId, status) => {
    const reason = benefitReasons[benefitId];
    if (!reason) {
      setError("Reason is required for approval or denial");
      toast.error("Reason is required for approval or denial");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}admin/accountant/accountant1/benefit/${benefitId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status, reason }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        const benefitsResponse = await fetch(`${process.env.REACT_APP_API_URL}admin/accountant/benefit/get-all-employee-benefits`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
        const benefitsData = await benefitsResponse.json();
        if (benefitsResponse.ok) {
          setEmployeeBenefits(benefitsData.data || []);
        }
      } else {
        setError(data.message);
        toast.error(data.message);
      }
    } catch (err) {
      setError("Server error while updating benefit");
      toast.error("Server error while updating benefit");
    } finally {
      setLoading(false);
    }
  };

  // if (loading) {
  //   return <div className="flex items-center justify-center h-screen bg-gray-100">Loading...</div>;
  // }

  if (!currentUser?._id || currentUser?.userType !== "adminAccountant1") {
    return (
      <div className="flex items-center justify-center h-screen text-red-500 bg-gray-100">
        Access denied. Accountant 1 privileges required.
      </div>
    );
  }

  if (error) {
    return <div className="flex items-center justify-center h-screen text-red-500 bg-gray-100">{error}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Accountant 1: Employee Benefits</h2>
      {employeeBenefits.map((employee) => (
        <div key={employee._id} className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h3 className="text-xl font-semibold text-gray-700 mb-4">
            {employee.firstName} {employee.lastName} ({employee.email})
          </h3>

          <h4 className="text-lg font-semibold text-gray-700 mb-2">Salary Breakdown</h4>
          <div className="overflow-x-auto mb-4">
            <table className="w-full bg-gray-50 border border-gray-200">
              <thead className="bg-orange-400 text-white">
                <tr>
                  <th className="py-3 px-4 text-left">Period</th>
                  <th className="py-3 px-4 text-left">Gross Salary (₦)</th>
                  <th className="py-3 px-4 text-left">Essential Benefit (10%)</th>
                  <th className="py-3 px-4 text-left">Health Benefit (5%)</th>
                </tr>
              </thead>
              <tbody>
                {employee.salary?.history?.length > 0 ? (
                  employee.salary.history.map((hist, i) => {
                    const benefits = {
                      essential: parseFloat(hist.grossSalary || 0) * 0.1,
                      health: parseFloat(hist.grossSalary || 0) * 0.05,
                    };
                    return (
                      <tr key={i} className="border-t hover:bg-gray-100">
                        <td className="py-3 px-4">{`${hist.month} ${hist.year}`}</td>
                        <td className="py-3 px-4">{(hist.grossSalary || 0).toLocaleString()}</td>
                        <td className="py-3 px-4">{benefits.essential.toLocaleString()}</td>
                        <td className="py-3 px-4">{benefits.health.toLocaleString()}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="4" className="py-3 px-4 text-center text-gray-500">
                      No salary history available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <h4 className="text-lg font-semibold text-gray-700 mb-2">Benefit Applications</h4>
          <div className="overflow-x-auto">
            <table className="w-full bg-gray-50 border border-gray-200">
              <thead className="bg-orange-400 text-white">
                <tr>
                  <th className="py-3 px-4 text-left">Type</th>
                  <th className="py-3 px-4 text-left">Amount (₦)</th>
                  <th className="py-3 px-4 text-left">Period</th>
                  <th className="py-3 px-4 text-left">Status</th>
                  <th className="py-3 px-4 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {employee.benefits?.length > 0 ? (
                  employee.benefits.map((benefit) => (
                    <tr key={benefit._id} className="border-t hover:bg-gray-100">
                      <td className="py-3 px-4 capitalize">{benefit.benefitType}</td>
                      <td className="py-3 px-4">{parseFloat(benefit.amountRequested).toLocaleString()}</td>
                      <td className="py-3 px-4">{benefit.benefitDescription.split(" for ")[1] || "N/A"}</td>
                      <td className="py-3 px-4">
                        {benefit.accountant1Status ? (
                          <div>
                            <span
                              className={
                                benefit.accountant1Status.status === "Approved" ? "text-green-600" : "text-red-600"
                              }
                            >
                              {benefit.accountant1Status.status}
                            </span>
                            <p className="text-sm text-gray-600">Reason: {benefit.accountant1Status.reason}</p>
                          </div>
                        ) : (
                          "Pending"
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {!benefit.accountant1Status && (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Reason"
                              value={benefitReasons[benefit._id] || ""}
                              onChange={(e) => setBenefitReasons({ ...benefitReasons, [benefit._id]: e.target.value })}
                              className="p-1 border border-gray-300 rounded mr-2"
                            />
                            <button
                              onClick={() => handleBenefitAction(benefit._id, "Approved")}
                              className="bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleBenefitAction(benefit._id, "Declined")}
                              className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                            >
                              Decline
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-3 px-4 text-center text-gray-500">
                      No benefit applications
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdminAccountant1;